import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3,
  Clock3,
  Home,
  FileText,
  Menu,
  MessageSquare,
  Tag,
  Package,
  ReceiptText,
  ShoppingBag,
  X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { isAdminUser, loadAdminDashboardData } from "../services/admin";
import PageHero from "../components/PageHero";
import heroImage from "../assets/zeamays.png";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);

const formatDate = (dateValue) => {
  if (!dateValue) return "Not available";

  return new Intl.DateTimeFormat("en-NG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(dateValue));
};

const statusStyles = {
  paid: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  pending: "bg-amber-50 text-amber-700 ring-amber-100",
  initialized: "bg-amber-50 text-amber-700 ring-amber-100",
  failed: "bg-red-50 text-red-700 ring-red-100",
  cancelled: "bg-red-50 text-red-700 ring-red-100",
  processing: "bg-blue-50 text-blue-700 ring-blue-100",
  out_for_delivery: "bg-purple-50 text-purple-700 ring-purple-100",
  delivered: "bg-green-50 text-green-700 ring-green-100",
};

const navItems = [
  { label: "Dashboard", href: "/admin", icon: Home },
  { label: "Orders", href: "/admin/orders", icon: ReceiptText },
  { label: "Blog", href: "/admin/blog", icon: FileText },
  { label: "Products", href: "/admin/products", icon: Package },
  { label: "Reviews", href: "/admin/reviews", icon: MessageSquare },
  { label: "Promotions", href: "/admin/promotions", icon: Tag },
];

const AdminDashboard = () => {
  const { user, loading } = useAuth();
  const [authorized, setAuthorized] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    orders: [],
    orderItems: [],
    contacts: [],
    totalProducts: 0,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const verifyAccess = async () => {
      if (!user) {
        if (!cancelled) {
          setAuthorized(false);
          setCheckingAccess(false);
        }
        return;
      }

      try {
        const adminAccess = await isAdminUser(user);
        if (!cancelled) {
          setAuthorized(adminAccess);
        }

        if (adminAccess) {
          const data = await loadAdminDashboardData();
          if (!cancelled) {
            setDashboardData(data);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Unable to load the admin dashboard.");
        }
      } finally {
        if (!cancelled) {
          setCheckingAccess(false);
        }
      }
    };

    verifyAccess();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const metrics = useMemo(() => {
    const totalRevenue = dashboardData.orders.reduce(
      (sum, order) => sum + Number(order.total_amount || 0),
      0
    );
    const pendingDeliveries = dashboardData.orders.filter((order) => {
      const trackingStatus = String(order.status || "").toLowerCase();
      return !["delivered", "cancelled", "failed"].includes(trackingStatus);
    }).length;

    return [
      {
        label: "Total Orders",
        value: dashboardData.orders.length.toLocaleString(),
        helper: "All store orders",
        icon: ShoppingBag,
      },
      {
        label: "Total Revenue",
        value: formatCurrency(totalRevenue),
        helper: "Gross order value",
        icon: BarChart3,
      },
      {
        label: "Total Products",
        value: Number(dashboardData.totalProducts || 0).toLocaleString(),
        helper: "From the store catalog",
        icon: Package,
      },
      {
        label: "Pending Deliveries",
        value: pendingDeliveries.toLocaleString(),
        helper: "Not delivered or cancelled",
        icon: Clock3,
      },
    ];
  }, [dashboardData.orders, dashboardData.totalProducts]);

  const totalRevenueValue = dashboardData.orders.reduce(
    (sum, order) => sum + Number(order.total_amount || 0),
    0
  );

  const recentOrders = useMemo(() => dashboardData.orders.slice(0, 8), [dashboardData.orders]);

  if (loading || checkingAccess) {
    return (
      <div className="min-h-screen bg-lime-50 px-6 py-28 text-center text-slate-700">
        <p className="text-lg font-semibold">Checking admin access...</p>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen bg-lime-50 px-6 py-28 text-center text-slate-700">
        <div className="mx-auto max-w-xl rounded-lg border border-red-200 bg-white p-8 shadow-sm">
          <h1 className="text-3xl font-semibold text-red-700">Access denied</h1>
          <p className="mt-4 text-lg">
            This dashboard is only available to authorized admin accounts.
          </p>
          <Link to="/" className="mt-6 inline-block rounded-md bg-red-700 px-5 py-3 text-white">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7fbef] pt-16 text-slate-900">
      <div className="lg:flex">
        <aside className="fixed bottom-0 left-0 top-16 hidden w-64 border-r border-lime-200 bg-white lg:block">
          <AdminSidebar />
        </aside>

        <header className="sticky top-16 z-30 border-b border-lime-200 bg-white/95 px-4 py-3 backdrop-blur lg:hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-700">Zea Mays</p>
              <h1 className="text-lg font-bold text-slate-950">Admin Dashboard</h1>
            </div>
            <button
              type="button"
              onClick={() => setMobileNavOpen((current) => !current)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-lime-200 text-red-700"
              aria-label="Toggle admin navigation"
            >
              {mobileNavOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
          {mobileNavOpen ? (
            <nav className="mt-3 grid gap-2">
              {navItems.map((item) => (
                <AdminNavLink key={item.label} item={item} onClick={() => setMobileNavOpen(false)} />
              ))}
            </nav>
          ) : null}
        </header>

        <main className="w-full px-4 py-6 sm:px-6 lg:ml-64 lg:px-8 lg:py-8">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8">
              <PageHero
                eyebrow="Store operations"
                title="Keep the catalog and orders moving."
                description="Use this command center to review orders, manage Pantry and ProBioDelights products, and keep the store ready for customers."
                image={heroImage}
                imageAlt="Zea Mays admin dashboard"
                primaryAction={{ to: "/admin/products", label: "Manage Products" }}
                secondaryAction={{ to: "/admin/orders", label: "Review Orders" }}
                showImageCaption={false}
                stats={[
                  { label: "Orders", value: dashboardData.orders.length.toLocaleString() },
                  { label: "Revenue", value: formatCurrency(totalRevenueValue) },
                  { label: "Products", value: Number(dashboardData.totalProducts || 0).toLocaleString() },
                  { label: "User", value: user?.email || "Admin" },
                ]}
                compact
              />
            </div>

            {error ? (
              <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {metrics.map((metric) => {
                const Icon = metric.icon;
                return (
                  <div key={metric.label} className="rounded-lg border border-lime-200 bg-white p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-slate-500">{metric.label}</p>
                        <p className="mt-3 text-2xl font-bold text-slate-950">{metric.value}</p>
                      </div>
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-lime-100 text-red-700">
                        <Icon size={22} />
                      </span>
                    </div>
                    <p className="mt-4 text-xs font-medium text-slate-500">{metric.helper}</p>
                  </div>
                );
              })}
            </section>

            <section className="mt-8 rounded-lg border border-lime-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.24em] text-red-700">
                    Content management
                  </p>
                  <h3 className="mt-2 text-xl font-bold text-slate-950">Blog Posts</h3>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                    Create, edit, publish, and delete website blog articles from the admin dashboard.
                  </p>
                </div>
                <Link
                  to="/admin/blog"
                  className="inline-flex items-center justify-center rounded-md bg-red-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-green-600"
                >
                  Manage Blog
                </Link>
              </div>
            </section>

            <section id="recent-orders" className="mt-8 rounded-lg border border-lime-200 bg-white shadow-sm">
              <div className="flex flex-col gap-2 border-b border-lime-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-xl font-bold text-slate-950">Recent Orders</h3>
                  <p className="text-sm text-slate-500">Latest store order records</p>
                </div>
                <span className="text-sm font-semibold text-red-700">{recentOrders.length} shown</span>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-[980px] w-full text-left text-sm">
                  <thead className="bg-lime-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Order Reference</th>
                      <th className="px-5 py-3">Customer Email</th>
                      <th className="px-5 py-3">Total Amount</th>
                      <th className="px-5 py-3">Payment Status</th>
                      <th className="px-5 py-3">Tracking Status</th>
                      <th className="px-5 py-3">Created Date</th>
                      <th className="px-5 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-lime-100">
                    {recentOrders.length ? (
                      recentOrders.map((order) => (
                        <tr key={order.id} className="align-middle">
                          <td className="px-5 py-4 font-semibold text-slate-950">
                            {order.payment_reference || order.id}
                          </td>
                          <td className="px-5 py-4 text-slate-600">{order.email || "No email"}</td>
                          <td className="px-5 py-4 font-semibold text-slate-950">
                            {formatCurrency(order.total_amount)}
                          </td>
                          <td className="px-5 py-4">
                            <StatusPill status={order.payment_status || order.status || "pending"} />
                          </td>
                          <td className="px-5 py-4">
                            <StatusPill status={order.status} />
                          </td>
                          <td className="px-5 py-4 text-slate-600">{formatDate(order.created_at)}</td>
                          <td className="px-5 py-4 text-right">
                            <Link
                              to="/admin/orders"
                              className="inline-flex items-center justify-center rounded-md bg-red-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-600"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-5 py-10 text-center text-slate-500">
                          No orders found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

const AdminSidebar = () => (
  <div className="flex h-full flex-col px-5 py-6">
    <div className="border-b border-lime-200 pb-6">
      <p className="text-xs font-bold uppercase tracking-[0.28em] text-red-700">Zea Mays</p>
      <h2 className="mt-2 text-2xl font-bold text-slate-950">Admin</h2>
    </div>
    <nav className="mt-6 grid gap-2">
      {navItems.map((item) => (
        <AdminNavLink key={item.label} item={item} />
      ))}
    </nav>
  </div>
);

const AdminNavLink = ({ item, onClick }) => {
  const Icon = item.icon;

  return (
    <Link
      to={item.href}
      onClick={onClick}
      className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-semibold text-slate-700 transition hover:bg-lime-100 hover:text-red-700"
    >
      <Icon size={18} />
      {item.label}
    </Link>
  );
};

const StatusPill = ({ status }) => {
  const normalized = String(status || "pending").toLowerCase();
  const label = normalized.replace(/_/g, " ");

  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-xs font-bold capitalize ring-1 ${
        statusStyles[normalized] || "bg-slate-50 text-slate-700 ring-slate-100"
      }`}
    >
      {label}
    </span>
  );
};

export default AdminDashboard;
